import { forwardRef, type SVGProps } from "react";
import { clsx } from "clsx";

export type LogoVariant = "brand" | "on-dark" | "mono-dark" | "mono-light";

type MarkProps = SVGProps<SVGSVGElement> & {
  variant?: LogoVariant;
  size?: number;
};

/**
 * Proofly mark — a tilted, fractured ledger hash.
 *
 * The mark reads as `#` at a glance (tax / records / numbers) but is
 * stylized for a modern, technical feel:
 *   • the whole glyph leans 8° to the left,
 *   • the top horizontal ruling is broken into two segments with a small
 *     gap — the "audit mark" where a redacted field would sit,
 *   • the bottom ruling is the brand-blue accent — the bottom line,
 *     verified.
 *
 * See branding/brand-identity.md for the canonical construction and usage
 * rules.
 */
export const LogoMark = forwardRef<SVGSVGElement, MarkProps>(function LogoMark(
  { variant = "brand", size = 32, className, ...rest },
  ref,
) {
  const c = variantColors(variant);
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      role="img"
      aria-label="Proofly"
      className={className}
      {...rest}
    >
      <g transform="rotate(-8 24 24)">
        {/* Two verticals — the ledger columns. */}
        <rect x="15" y="6" width="5" height="36" rx="2" fill={c.ruling} />
        <rect x="28" y="6" width="5" height="36" rx="2" fill={c.ruling} />
        {/* Top horizontal — split into two segments with a 4-unit gap.
            The break is what makes the mark feel technical + modern
            without sacrificing the `#` read. */}
        <rect x="6" y="15" width="13" height="5" rx="2" fill={c.ruling} />
        <rect x="23" y="15" width="19" height="5" rx="2" fill={c.ruling} />
        {/* Bottom horizontal — brand accent. */}
        <rect x="6" y="27" width="36" height="5" rx="2" fill={c.accent} />
      </g>
    </svg>
  );
});

function variantColors(variant: LogoVariant) {
  switch (variant) {
    case "on-dark":
      return { ruling: "#FFFFFF", accent: "#5E7FFF" };
    case "mono-dark":
      return { ruling: "#0A0F1E", accent: "#0A0F1E" };
    case "mono-light":
      return { ruling: "#FFFFFF", accent: "#FFFFFF" };
    case "brand":
    default:
      return { ruling: "#0A0F1E", accent: "#2E5BFF" };
  }
}

type LogoProps = {
  variant?: LogoVariant;
  size?: number;
  showWordmark?: boolean;
  label?: string;
  className?: string;
};

export function Logo({
  variant = "brand",
  size = 28,
  showWordmark = true,
  label,
  className,
}: LogoProps) {
  const wordmarkColor = wordmarkColorFor(variant);
  return (
    <span
      className={clsx("inline-flex items-center gap-2 select-none", className)}
      aria-label={label ?? "Proofly"}
      role="img"
    >
      <LogoMark variant={variant} size={size} aria-hidden="true" />
      {showWordmark && (
        <span
          aria-hidden="true"
          style={{
            fontFamily: '"Inter Tight", "Inter", system-ui, sans-serif',
            fontWeight: 600,
            letterSpacing: "-0.025em",
            fontSize: `${Math.round(size * 0.78)}px`,
            lineHeight: 1,
            color: wordmarkColor,
          }}
        >
          proofly
        </span>
      )}
    </span>
  );
}

function wordmarkColorFor(variant: LogoVariant) {
  switch (variant) {
    case "on-dark":
    case "mono-light":
      return "#FFFFFF";
    case "mono-dark":
      return "#0A0F1E";
    case "brand":
    default:
      return "#0A0F1E";
  }
}
